import React, { useMemo } from 'react';
import { Navbar, Nav } from 'reactstrap';
import Context from '../Contexts';
import { AnyStep } from '..';
import WizardStep from '../Step';
import produce from 'immer';

type StepNode = AnyStep & { steps: StepNode[] };
const setIn = (nodes: StepNode[], step: AnyStep) =>
  produce(nodes, draft => {
    const index = draft.findIndex(n => n.name === step.name);
    if (index < 0) draft.push({ ...step, steps: [] });
    return draft;
  });

const getStep = (steps: AnyStep[], name: string) => {
  const index = steps.findIndex(n => n.name === name);

  if (index < 0) throw new Error('could not find step in steps');
  return steps[index];
};

const setInRecursive = (
  nodes: StepNode[],
  step: AnyStep,
  steps: AnyStep[],
  path: string[],
) =>
  produce(nodes, draft => {
    const [current, ...rest] = path;
    if (!current) return draft;
    if (current === step.name) {
      draft = setIn(draft, step);
      return draft;
    }
    const steppingStone = getStep(steps, current);
    draft = setIn(draft, steppingStone);
    const index = draft.findIndex(n => n.name === current);

    draft[index].steps = setInRecursive(draft[index].steps, step, steps, rest);
    return draft;
  });

const createStepForest = (steps: AnyStep[]): StepNode[] => {
  let stepNodes: StepNode[] = [];
  steps.forEach(s => {
    stepNodes = produce(stepNodes, draft => {
      const path = s.name.split('.').reduce<string[]>((pv, cv, index) => {
        if (index === 0) {
          pv.push(cv);
          return pv;
        }
        pv.push([pv[pv.length - 1], cv].join('.'));
        return pv;
      }, []);
      if (path.length === 1) {
        draft = setIn(draft, s);
        return draft;
      }

      draft = setInRecursive(draft, s, steps, path);
      return draft;
    });
  });
  return stepNodes;
};

const RenderComponent = (props: StepNode) => {
  const { kind, steps, ...rest } = props;
  return (
    <Context.Consumer>
      {({ setActivePage, setActiveGroup, activeGroup = '', activePage = '' }) =>
        kind === 'group' ? (
          <WizardStep
            {...rest}
            onClick={() => setActiveGroup(rest.name)}
            active={
              activePage.startsWith(rest.name) ||
              activeGroup.startsWith(rest.name)
            }>
            {steps.map(s => (
              <RenderComponent key={s.name} {...s} />
            ))}
          </WizardStep>
        ) : (
          <WizardStep {...rest} onClick={() => setActivePage(rest.name)} />
        )
      }
    </Context.Consumer>
  );
};
const Banner = ({ steps }: { steps: AnyStep[] }) => {
  const forest = useMemo(() => createStepForest(steps), [steps]);

  return forest.length >= 0 ? (
    <>
      {forest.map(step => (
        <RenderComponent key={step.name} {...step} />
      ))}
    </>
  ) : null;
};

const WizardBanner = () => {
  return (
    <Navbar sticky="top" className={'mb-3 sticky-wizard-top'} color={'light'}>
      <Nav className="w-100" justified>
        <Context.Consumer>
          {({ steps }) => <Banner steps={steps} />}
        </Context.Consumer>
      </Nav>
    </Navbar>
  );
};

export default WizardBanner;
